import * as Constants from "common/constants";
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWorkflowHistoriesRequest } from "../store/slices/workflows/workflowHistoriesSlice";
import { fetchFormByID } from "../store/thunks/formThunks";
import { fetchCompanyDriversThunk, fetchTrucksThunk, fetchDatabaseRecord, fetchWorkflowTemplatesThunk, patchDatabaseRecord } from "../store/thunks/mdsThunks";
import {
	deletePendingTaskThunk,
	fetchFormByUserTaskThunk,
	fetchTaskVariables,
	postRegeneratePDFThunk,
	putAssigneeForTaskThunk,
	putProcessInstanceVariableThunk,
	cloneWorkflowThunk,
} from "../store/thunks/workflowThunks";
import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import Loader from "../components/common/custom-component/Loader";
import CustomPagination from "../components/common/CustomPagination";
import DataContainer from "../components/common/DataContainer/DataContainer";

import { CalendarToday as CalendarIcon } from "@mui/icons-material";
import { Alert, Box, Typography, Stack, TextField, MenuItem, Divider } from "@mui/material";

import jQuery from "jquery";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSearchFilter } from "hooks/useSearchFilter";
import SearchFilterBar from "components/common/SearchFilterBar";
import { useToast } from "../components/common/custom-component/ToastProvider";
import { useAuth } from "../hooks/useAuth";
import { useTableColumns } from "../hooks/useTableColumns";
import { workflowHistoryReducer, initialState } from "../reducers/workflowHistoryReducer";
import { logger } from "helpers/logger";

export default function WorkflowHistoryPageRemastered() {
	const { success: ToastSuccess, error: ToastError } = useToast();
	const [t] = useTranslation();
	const keycloakAuth = useAuth();
	const navigate = useNavigate();
	const hasDataInStore = useRef(false);

	const user = keycloakAuth.user;
	const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);
	const recordsPerPage = 10;

	// Use reducer for consolidated state management
	const [state, dispatchState] = useReducer(workflowHistoryReducer, initialState);

	const { data: WorkflowHistoryData, loading, error } = useSelector((state) => ({ data: state.workflowHistories?.data?.data, loading: state.workflowHistories?.loading, error: state.workflowHistories?.error }));
	const totalPages = Math.ceil((WorkflowHistoryData?.totalCount ?? 0) / recordsPerPage);

	// Keep pagination state separate (frequently changes, less benefit from consolidation)
	const [currentPage, setCurrentPage] = useState(() => {
		const savedPage = sessionStorage.getItem("pageIndex");
		return savedPage ? parseInt(savedPage) : 1;
	});
	// Add ref to track current page for use in event handlers without triggering re-renders
	const currentPageRef = useRef(currentPage);

	// Update ref whenever currentPage changes
	useEffect(() => {
		currentPageRef.current = currentPage;
	}, [currentPage]);

	// Add state to track deleted record IDs
	const [deletedRecordIds, setDeletedRecordIds] = useState(new Set());
	// Filter out deleted records for display

	const displayData = useMemo(
		() => WorkflowHistoryData?.workflows?.filter((record) => !deletedRecordIds.has(record.rootProcessInstanceId)) || [],
		[WorkflowHistoryData, deletedRecordIds] // Dependencies
	);

	// Keep fetched data arrays as separate state (easier to manage)
	const [transporterDrivers, setTransporterDrivers] = useState([]);
	const [transporterTrucks, setTransporterTrucks] = useState([]);
	const [templates, setTemplates] = useState([]);

	const dispatch = useDispatch();

	// #region FILTERS & SORT

	const { setSearchQuery } = useSearchFilter(displayData, ["generatedEmanifestID", "workflowName", "taskName", "userTaskAssignee", "taskId", "processInstanceId"], null);

	const applyFilters = () => {
		try {
			const newSearchParams = {
				searchTerm: state.table.searchInput?.trim() || "",
				startDate: toDDMMYYYY(state.table.startDateInput),
				endDate: toDDMMYYYY(state.table.endDateInput),
			};

			dispatchState({ type: "SET_SEARCH_PARAMS", payload: newSearchParams });
			setSearchQuery(newSearchParams.searchTerm);
			setCurrentPage(1);

			dispatch(
				fetchWorkflowHistoriesRequest({
					assignee: user.id,
					pageNumber: 1,
					recordsByPage: recordsPerPage,
					searchParam: newSearchParams.searchTerm,
					startDate: newSearchParams.startDate,
					endDate: newSearchParams.endDate,
					sortField: state.table.sortField,
					sortDirection: state.table.sortDirection,
				})
			);
		} catch (err) {
			logger.error("Error in applyFilters:", err);
		}
	};

	const clearFilters = () => {
		const newSearchParams = { searchTerm: "", startDate: "", endDate: "" };

		dispatchState({ type: "SET_SEARCH_INPUT", payload: "" });
		dispatchState({ type: "SET_START_DATE_INPUT", payload: "" });
		dispatchState({ type: "SET_END_DATE_INPUT", payload: "" });
		dispatchState({ type: "SET_SEARCH_PARAMS", payload: newSearchParams });
		setSearchQuery("");
		setCurrentPage(1);

		dispatch(
			fetchWorkflowHistoriesRequest({
				assignee: user.id,
				pageNumber: 1,
				recordsByPage: recordsPerPage,
				searchParam: newSearchParams.searchTerm,
				startDate: newSearchParams.startDate,
				endDate: newSearchParams.endDate,
				sortField: state.table.sortField,
				sortDirection: state.table.sortDirection,
			})
		);
	};

	const handleSort = (...args) => {
		let column = args[0];
		let direction = args[1];

		if (!column) return;

		let nextDirection;

		if (direction) {
			nextDirection = column === state.table.sortField && direction === state.table.sortDirection ? (state.table.sortDirection === "asc" ? "desc" : "asc") : direction;
		} else {
			nextDirection = column === state.table.sortField ? (state.table.sortDirection === "asc" ? "desc" : "asc") : "asc";
		}

		dispatchState({ type: "SET_SORT", payload: { field: column, direction: nextDirection } });
		setCurrentPage(1);

		dispatch(
			fetchWorkflowHistoriesRequest({
				assignee: user.id,
				pageNumber: 1,
				recordsByPage: recordsPerPage,
				searchParam: state.table.searchParams.searchTerm,
				startDate: state.table.searchParams.startDate,
				endDate: state.table.searchParams.endDate,
				sortField: column,
				sortDirection: nextDirection,
			})
		);
	};

	const handleChangeRecordsPerPage = (e) => {
		const value = Number(e.target.value) || 10;
		// recordsPerPage is a constant, not a state variable
		setCurrentPage(1);

		// fetch immediately with the fresh value (keeps your current structure)
		dispatch(
			fetchWorkflowHistoriesRequest({
				assignee: user.id,
				pageNumber: 1,
				recordsByPage: value,
				searchParam: state.table.searchParams.searchTerm,
				startDate: state.table.searchParams.startDate,
				endDate: state.table.searchParams.endDate,
				sortField: state.table.sortField,
				sortField: state.table.sortField,
			})
		);
	};

	// #endregion

	// #region HELPER FUNCTION

	// Helper function to fetch and filter transporter drivers

	// Safe date formatting helper
	const toDDMMYYYY = (isoLike) => {
		if (!isoLike || typeof isoLike !== "string") return "";
		const parts = isoLike.split("-");
		if (parts.length !== 3) return "";
		const [y, m, d] = parts;
		if (!y || !m || !d) return "";
		return `${d}-${m}-${y}`;
	};

	const fetchTransporterDrivers = useCallback(
		async (companyId) => {
			try {
				const driversResponse = await dispatch(fetchCompanyDriversThunk({ companyId }));

				if (fetchCompanyDriversThunk.rejected.match(driversResponse)) {
					logger.error("Error fetching drivers:", driversResponse?.payload?.data);
					return null;
				}

				const allRecords = driversResponse?.payload?.data;

				// Extract all drivers with roleId 13
				const transporterDrivers = allRecords.filter((record) => record.rolesResponseList?.some((role) => role.roleId === 13));

				return transporterDrivers;
			} catch (error) {
				logger.error("Error in fetchTransporterDrivers:", error);
				return null;
			}
		},
		[dispatch]
	);

	const fetchTransporterTrucks = useCallback(
		async (companyId) => {
			try {
				const trucksResponse = await dispatch(fetchTrucksThunk({ companyId }));

				if (fetchTrucksThunk.rejected.match(trucksResponse)) {
					logger.error("Error fetching Trucks:", trucksResponse.payload?.data);
					return null;
				}

				const transporterTrucks = trucksResponse.payload?.data;
				return transporterTrucks;
			} catch (error) {
				logger.error("Error in fetchTransporterTrucks:", error);
				return null;
			}
		},
		[dispatch]
	);

	const fetchTaskId = useCallback(
		async (processInstanceId) => {
			try {
				const taskResponse = await dispatch(fetchFormByUserTaskThunk({ processInstanceId }));

				if (fetchFormByUserTaskThunk.rejected.match(taskResponse)) {
					logger.error("Error fetching Task:", taskResponse.payload);
					return null; // or return empty array: []
				}

				const taskRec = taskResponse.payload?.data;

				//console.log('taskRec:', taskRec);
				//console.log('Number of taskRec:', taskRec.length);

				return taskRec.getFormByTaskResponse.taskId;
			} catch (error) {
				logger.error("Error in fetchFormByUserTaskThunk:", error);
				return null; // or return empty array: []
			}
		},
		[dispatch]
	);

	const getTaskVariables = useCallback(
		async (processInstanceId) => {
			try {
				const variablesResult = await dispatch(fetchTaskVariables({ processInstanceId }));

				if (fetchTaskVariables.rejected.match(variablesResult)) {
					logger.error("Error fetching variables:", variablesResult.payload);
					return;
				}

				const variables = variablesResult.payload?.data;

				/* console.log('taskVariables:', variables);
			console.log('Number of taskVariables:', variables.length); */

				return variables;
			} catch (error) {
				logger.error("Error in fetchTaskVariables:", error);
				return null; // or return empty array: []
			}
		},
		[dispatch]
	);

	const getFacilityName = useCallback(
		async (processInstanceId) => {
			try {
				const variables = await getTaskVariables(processInstanceId);
				const facilityName = variables.find((rec) => rec.name === "facilityInformation")?.value?.entityName;
				return facilityName;
			} catch (error) {
				logger.error("Error in getFacilityName:", error);
				return null; // or return empty array: []
			}
		},
		[getTaskVariables]
	);
	// Helper: Prepare truck info
	const prepareTruckInfo = useCallback(
		async (plateChange, selectedRecord) => {
			logger.info("Plate changed!");
			logger.info(`Old Plate: ${plateChange.oldValue}`);
			logger.info(`New Plate: ${plateChange.newValue}`);

			const matchedTruck = transporterTrucks.find((truck) => truck.plateNumber === plateChange.newValue);

			if (!matchedTruck) {
				logger.error("No matching truck found");
				return null;
			}

			const currentTruckInfo = selectedRecord?.truckInfo ? JSON.parse(selectedRecord.truckInfo) : {};

			// Merge matched truck data with existing structure
			const updatedTruckInfo = Object.keys(currentTruckInfo).reduce(
				(acc, key) => ({
					...acc,
					[key]: matchedTruck[key] ?? currentTruckInfo[key],
				}),
				{}
			);

			logger.info("Updated Truck Info:", updatedTruckInfo);
			return updatedTruckInfo;
		},
		[transporterTrucks]
	);

	// Helper: Prepare assignee data
	const prepareAssigneeData = useCallback(
		async (driverChange, selectedRecord) => {
			logger.info("Driver changed!");
			logger.info(`Old Driver: ${driverChange.oldValue}`);
			logger.info(`New Driver: ${driverChange.newValue}`);

			const newDriver = transporterDrivers.find((driver) => driver.userId === driverChange.newValue);
			const oldDriver = transporterDrivers.find((driver) => driver.userId === driverChange.oldValue);

			if (!newDriver) {
				logger.error("New driver not found");
				return null;
			}

			const taskId = await fetchTaskId(selectedRecord.rootProcessInstanceId);
			const facilityName = await getFacilityName(selectedRecord.rootProcessInstanceId);

			return {
				taskId,
				userId: driverChange.newValue,
				tableName: selectedRecord.applicationID,
				fieldName: "driverUserId",
				createdId: selectedRecord.createdId,
				processId: selectedRecord.rootProcessInstanceId,
				userInfo: {
					loggedInUserFullName: user.firstName + user.lastName,
				},
				driverInfo: {
					oldDriver: {
						fullName: oldDriver?.fullName,
						phoneNumber: oldDriver?.mobileNumber,
					},
					newDriver: {
						fullName: newDriver?.fullName,
						phoneNumber: newDriver?.mobileNumber,
					},
				},
				selectedCompany: facilityName,
			};
		},
		[transporterDrivers, fetchTaskId, getFacilityName, user]
	);

	// Helper: Close dialog and reset
	const closeEditDialog = useCallback(
		(refreshData = false) => {
			dispatchState({ type: "CLOSE_EDIT_DIALOG" });
			dispatchState({
				type: "UPDATE_FORM_DATA",
				payload: {
					formName: "reassignFormData",
					data: { Driver: null, Plate: null },
				},
			});

			// Refresh the workflow history after successful deletion
			// Only refresh if explicitly requested
			if (refreshData) {
				dispatch(
					fetchWorkflowHistoriesRequest({
						assignee: user.id,
						pageNumber: currentPage,
						recordsByPage: recordsPerPage,
						searchParam: state.table.searchParams.searchTerm,
						startDate: state.table.searchParams.startDate,
						endDate: state.table.searchParams.endDate,
						sortField: state.table.sortField,
						sortDirection: state.table.sortDirection,
					})
				);
			}
		},
		[dispatch, user.id, currentPage, recordsPerPage, state.table]
	);

	// Helper: Close dialog and reset
	const closeTemplateDialog = useCallback(() => {
		dispatchState({ type: "CLOSE_REGENERATE_DIALOG" });
		dispatchState({
			type: "UPDATE_FORM_DATA",
			payload: {
				formName: "templateFormData",
				data: { Template: null },
			},
		});
	}, []);

	// Helper: Get changed fields
	const getChangedFields = useCallback((currentData, initialData) => {
		const changedFields = {};
		Object.keys(currentData).forEach((key) => {
			if (currentData[key] !== initialData[key]) {
				changedFields[key] = {
					oldValue: initialData[key],
					newValue: currentData[key],
				};
			}
		});
		return changedFields;
	}, []);
	// Helper: Set Assignee
	const setAssignee = useCallback(
		async (assigneeData) => {
			try {
				const setAssigneeRes = await dispatch(putAssigneeForTaskThunk(assigneeData));

				if (putAssigneeForTaskThunk.rejected.match(setAssigneeRes)) {
					logger.error("Error setting assignee:", setAssigneeRes.payload);
					return null; // or return empty array: []
				}

				const assigneeRes = setAssigneeRes.payload?.data;

				//console.log('Transporter Trucks:', transporterTrucks);
				//console.log('Number of trucks found:', transporterTrucks.length);

				return assigneeRes;
			} catch (error) {
				logger.error("Error in putAssigneeForTaskThunk:", error);
				return null; // or return empty array: []
			}
		},
		[dispatch]
	);
	// Helper: Update truck Info
	const updateTruckInfo = useCallback(
		async (jsonRequest, JsonToPatch) => {
			try {
				const putProcessInstRes = await dispatch(putProcessInstanceVariableThunk(jsonRequest));

				if (putProcessInstanceVariableThunk.rejected.match(putProcessInstRes)) {
					logger.error("Error putting process instance variable:", putProcessInstRes.payload);
					return null; // or return empty array: []
				}

				// Wait for database update to complete
				const patchResult = await dispatch(patchDatabaseRecord({ JsonToPatch: JsonToPatch }));
				if (patchDatabaseRecord.rejected.match(patchResult)) {
					logger.error("Error patching database record:", patchResult.payload);
					return null; // or return empty array: []
				}
			} catch (error) {
				logger.error("Error in putProcessInstanceVariableThunk:", error);
				return null; // or return empty array: []
			}
		},
		[dispatch]
	);

	const fetchTemplates = useCallback(
		async (applicationId) => {
			try {
				const templateResponse = await dispatch(fetchWorkflowTemplatesThunk({ applicationID: applicationId }));

				if (fetchWorkflowTemplatesThunk.rejected.match(templateResponse)) {
					logger.error("Error fetching Templates:", templateResponse.payload);
					return null;
				}

				const templates = templateResponse.payload.data?.dataBaseContent;

				return templates;
			} catch (error) {
				logger.error("Error in fetchWorkflowTemplatesThunk:", error);
				return null;
			}
		},
		[dispatch]
	);

	const generatePDF = useCallback(
		async (applicationId, templateId, tableId) => {
			try {
				const generatePDFResponse = await dispatch(
					postRegeneratePDFThunk({
						applicationID: applicationId,
						templateId: templateId,
						tableId: tableId,
					})
				);

				if (postRegeneratePDFThunk.rejected.match(generatePDFResponse)) {
					logger.error("Error generating PDF:", generatePDFResponse.payload);
					return null;
				}

				const generatePDFResult = generatePDFResponse?.payload?.data;

				return generatePDFResult;
			} catch (error) {
				logger.error("Error in postRegeneratePDFThunk:", error);
				return null;
			}
		},
		[dispatch]
	);

	// #endregion

	// #region REGERATE PROCESS

	const handleRegenerate = useCallback(
		async (extractedFields, templateId) => {
			try {
				logger.info("extractedFields: ", extractedFields);
				const res = await generatePDF(extractedFields.applicationID, templateId, extractedFields.tableId);
			} catch (err) {
				logger.error("Error updating record:", err);
				throw err;
			}
		},
		[generatePDF]
	);

	const handleRegerateClick = useCallback(
		async (row) => {
			// Show backdrop
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
			const extractedFields = (({ applicationID, tableId }) => ({
				applicationID,
				tableId,
			}))(row);
			dispatchState({ type: "SET_FIELDS_EXTRACTED", payload: extractedFields });

			const templateRecs = await fetchTemplates(extractedFields.applicationID);
			if (!templateRecs) {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
				return;
			}
			setTemplates(templateRecs);

			if (templateRecs.length > 1) {
				const initialData = {
					Template: templateRecs.at(0)?.id || "",
				};
				dispatchState({
					type: "UPDATE_FORM_DATA",
					payload: { formName: "templateFormData", data: initialData },
				});
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
				dispatchState({ type: "OPEN_REGENERATE_DIALOG", payload: extractedFields });
			} else if (templateRecs.length == 1) {
				const templateId = templateRecs.at(0)?.id;
				await handleRegenerate(extractedFields, templateId);
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			} else {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
				return;
			}
		},
		[fetchTemplates, handleRegenerate]
	);

	const handleCancelRegenerate = useCallback(() => {
		closeTemplateDialog();
	}, [closeTemplateDialog]);

	const handleConfirmRegenerate = useCallback(async () => {
		if (!state.dialogs.fieldsExtracted) return;
		const selectedTemplate = state.forms.templateFormData.Template;
		logger.info("Selected template:", selectedTemplate);
		await handleRegenerate(state.dialogs.fieldsExtracted, selectedTemplate);

		closeTemplateDialog();
	}, [state.dialogs.fieldsExtracted, state.forms.templateFormData.Template, handleRegenerate, closeTemplateDialog]);
	// #endregion

	// #region EDIT PROCESS

	// Simplified handleEditClick
	const handleEditClick = useCallback(
		async (row) => {
			dispatchState({ type: "SET_SELECTED_RECORD", payload: row });
			// Show backdrop immediately when edit is clicked
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
			const { rootProcessInstanceId: processInstanceId, taskName: name, applicationID, generatedEmanifestID, createdId, assignedUserId, truckInfo } = row;

			const transporterDriversRecs = await fetchTransporterDrivers(selectedCompany.companyId);

			if (!transporterDriversRecs) {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } }); // Hide backdrop if fetch fails
				return;
			}
			setTransporterDrivers(transporterDriversRecs);

			const transporterTrucksRecs = await fetchTransporterTrucks(selectedCompany.companyId);
			if (!transporterTrucksRecs) {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } }); // Hide backdrop if fetch fails
				return;
			}
			setTransporterTrucks(transporterTrucksRecs);

			const initialData = {
				Driver: row.assignedUserId || null,
				Plate: row.truckInfo ? JSON.parse(row.truckInfo).plateNumber ?? null : null,
			};

			dispatchState({
				type: "UPDATE_FORM_DATA",
				payload: { formName: "reassignFormData", data: initialData },
			});
			dispatchState({
				type: "UPDATE_FORM_DATA",
				payload: { formName: "initialFormData", data: initialData },
			});
			// Hide backdrop and show dialog
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			dispatchState({ type: "OPEN_EDIT_DIALOG", payload: { record: row, fields: null } });
		},
		[selectedCompany, fetchTransporterDrivers, fetchTransporterTrucks]
	);

	const handleCancelEdit = useCallback(() => {
		dispatchState({ type: "CLOSE_EDIT_DIALOG" });
		dispatchState({
			type: "UPDATE_FORM_DATA",
			payload: {
				formName: "reassignFormData",
				data: { Driver: null, Plate: null },
			},
		});
	}, []);

	const handleEdit = useCallback(
		async (selectedRec, assigneeData, truckInfo) => {
			try {
				const updates = [];

				// Process driver reassignment
				if (assigneeData) {
					logger.info("Processing driver reassignment:", assigneeData);
					// Call your API for driver update
					const driverUpdateResult = await setAssignee(assigneeData);
					ToastSuccess("Driver reassigned successfully!");
					//updates.push({ type: 'driver', success: true, data: driverUpdateResult });
				}

				// Process truck update
				if (truckInfo) {
					logger.info("Processing truck update:", truckInfo);
					// Call your API for truck update
					const jsonRequest = {
						processInstanceId: selectedRec.rootProcessInstanceId,
						variableName: "truckInfo",
						jsonData: {
							value: JSON.stringify(truckInfo, null, 1),
							type: "Object",
							valueInfo: { objectTypeName: "java.util.LinkedHashMap", serializationDataFormat: "application/json" },
						},
					};

					const JsonToPatch = {
						tableName: selectedRec.applicationID,
						fields: {
							truckInfo: JSON.stringify(truckInfo, null, 1)
						},
						matchField: "id",
						matchValue: selectedRec.createdId
					};

					const truckUpdateResult = await updateTruckInfo(jsonRequest, JsonToPatch);
					ToastSuccess("Truck reassigned successfully!");
					//updates.push({ type: 'truck', success: true, data: truckUpdateResult });
				}

				/* if (updates.length === 0) {
				console.warn('No updates to process');
				return;
			} */

				//console.log('All updates completed:', updates);
				// Show success message to user
				// Refresh your data/table
			} catch (err) {
				logger.error("Error updating record:", err);
				// Show error message to user
				throw err;
			}
		},
		[setAssignee, updateTruckInfo]
	);

	const handleConfirmEdit = useCallback(async () => {
		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
		if (!state.dialogs.selectedRecord) return;

		logger.info("Selected Record:", state.dialogs.selectedRecord);
		logger.info("Current Form Data:", state.forms.reassignFormData);

		// Get only changed fields
		const changedFields = getChangedFields(state.forms.reassignFormData, state.forms.initialFormData);
		logger.info("Changed Fields:", changedFields);

		if (Object.keys(changedFields).length === 0) {
			logger.info("No changes detected");
			closeEditDialog();
			return;
		}

		// Prepare data based on what changed
		let truckInfoToUpdate = null;
		let assigneeDataToUpdate = null;

		// Handle Plate change
		if (changedFields.Plate) {
			truckInfoToUpdate = await prepareTruckInfo(changedFields.Plate, state.dialogs.selectedRecord);
		}

		// Handle Driver change
		if (changedFields.Driver) {
			assigneeDataToUpdate = await prepareAssigneeData(changedFields.Driver, state.dialogs.selectedRecord);
		}

		// Send to handleEdit with only the data that changed
		await handleEdit(state.dialogs.selectedRecord, assigneeDataToUpdate, truckInfoToUpdate);

		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		closeEditDialog(true);
	}, [
		state.dialogs.selectedRecord,
		state.forms.reassignFormData,
		state.forms.initialFormData,
		getChangedFields,
		prepareTruckInfo,
		prepareAssigneeData,
		handleEdit,
		closeEditDialog,
	]);

	const handleFieldChange = useCallback(
		(formName) => (fieldName, value) => {
			dispatchState({
				type: "UPDATE_FORM_DATA",
				payload: {
					formName,
					data: { ...state.forms[formName], [fieldName]: value },
				},
			});
		},
		[state.forms]
	);

	const driverOptions =
		transporterDrivers?.map((driver) => ({
			value: driver.userId,
			label: driver.fullName,
		})) || [];

	const trucksOptions =
		transporterTrucks?.map((truck) => ({
			value: truck.plateNumber, //it should be truck.id, but in the row data -> truck info doest have id
			label: truck.plateNumber,
		})) || [];

	const templateOptions =
		templates?.map((template) => ({
			value: template.id,
			label: template.templateName,
		})) || [];

	// #endregion
	// #region DELETE PROCESS
	const handleDeleteClick = useCallback((rootProcessInstanceId, taskName, applicationID, createdId) => {
		dispatchState({
			type: "OPEN_DELETE_DIALOG",
			payload: { rootProcessInstanceId, taskName, applicationID, createdId },
		});
	}, []);

	const handleCancelDelete = useCallback(() => {
		dispatchState({ type: "CLOSE_DELETE_DIALOG" });
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (state.dialogs.selectedRecord) {
			handleRemove(
				state.dialogs.selectedRecord.rootProcessInstanceId,
				state.dialogs.selectedRecord.taskName,
				state.dialogs.selectedRecord.applicationID,
				state.dialogs.selectedRecord.createdId
			);
		}
		dispatchState({ type: "CLOSE_DELETE_DIALOG" });
	}, [state.dialogs.selectedRecord]);

	const handleRemove = async (rootProcessInstanceId, taskName, table_name, createdId) => {
		try {
			const result = await dispatch(deletePendingTaskThunk({ rootProcessInstanceId }));

			if (deletePendingTaskThunk.fulfilled.match(result) && result.payload?.data?.status === 1) {
				// remove from UI immediately
				setDeletedRecordIds((prev) => new Set(prev).add(rootProcessInstanceId));
				if (createdId != null && createdId !== "") {
					const jsonRequest = {
						tableName: table_name,
						fields: {
							taskStatus: "Deleted"
						},
						matchField: "id",
						matchValue: createdId
					};

					// Wait for database update to complete
					const patchResult = await dispatch(patchDatabaseRecord({ JsonToPatch: jsonRequest }));

					if (patchDatabaseRecord.fulfilled.match(patchResult)) {
						logger.info("Task status updated successfully");
					}
				}
				// Show refreshing indicator and refresh the workflow history
				//setIsBackgroundRefreshing(true);
				// Refresh the workflow history after successful deletion
				dispatch(
					fetchWorkflowHistoriesRequest({
						assignee: user.id,
						pageNumber: currentPage,
						recordsByPage: recordsPerPage,
						searchParam: state.table.searchParams.searchTerm,
						startDate: state.table.searchParams.startDate,
						endDate: state.table.searchParams.endDate,
						sortField: state.table.sortField,
						sortField: state.table.sortField,
					})
				);
			} else {
				logger.error("Failed to delete task");
			}
		} catch (err) {
			logger.error("Error removing task:", err);
		}
	};
	// #endregion

	useEffect(() => {
		dispatch(
			fetchWorkflowHistoriesRequest({
				assignee: keycloakAuth.user.id,
				pageNumber: currentPage,
				recordsByPage: recordsPerPage,
				searchParam: state.table.searchParams.searchTerm,
				startDate: state.table.searchParams.startDate,
				endDate: state.table.searchParams.endDate,
				sortField: state.table.sortField,
				sortDirection: state.table.sortDirection,
			})
		);
		sessionStorage.removeItem("pageIndex");
	}, [dispatch, keycloakAuth.user.id, currentPage, state.table.searchParams, state.table.sortField, state.table.sortDirection]);

	// Separate useEffect to turn off background refresh when loading completes
	useEffect(() => {
		const hasExistingData = displayData?.length > 0;
		if (!loading && state.loading.isBackgroundRefreshing) {
			dispatchState({ type: "SET_LOADING", payload: { key: "isBackgroundRefreshing", value: false } });
			setDeletedRecordIds(new Set()); // Clear deleted IDs after refresh
		}
		if (hasExistingData) {
			hasDataInStore.current = hasExistingData;
		}
		if (loading && hasExistingData) {
			dispatchState({ type: "SET_LOADING", payload: { key: "isBackgroundRefreshing", value: true } });
		}
	}, [loading, state.loading.isBackgroundRefreshing]);

	const parseIfJson = (data) => {
		try {
			const v = JSON.parse(data);
			return typeof v === "string" ? parseIfJson(v) : v;
		} catch {
			return data;
		}
	};

	// #region CLONE WORKFLOW
	const handleCloneClick = useCallback((applicationId, rootProcessInstanceId) => {
		dispatchState({
			type: "OPEN_CLONE_DIALOG",
			payload: { applicationId, rootProcessInstanceId },
		});
	}, []);

	const handleCancelClone = useCallback(() => {
		dispatchState({ type: "CLOSE_CLONE_DIALOG" });
	}, []);

	const handleConfirmClone = useCallback(() => {
		if (state.dialogs.selectedRecord) {
			handleClone(state.dialogs.selectedRecord.applicationID || state.dialogs.selectedRecord.applicationId, state.dialogs.selectedRecord.rootProcessInstanceId);
		}
		dispatchState({ type: "CLOSE_CLONE_DIALOG" });
	}, [state.dialogs.selectedRecord]);

	const handleClone = async (applicationId, processInstanceId) => {
		try {
			const result = await dispatch(cloneWorkflowThunk({ applicationId: applicationId, assignee: keycloakAuth.user.id, processInstanceId: processInstanceId }));
			if (cloneWorkflowThunk.fulfilled.match(result) && result.payload?.data?.httpResponse.status == 1) {
				const task = result?.payload?.data?.startProcessResponse?.getCurrentUserTaskResponse;
				const taskInfo = task?.getFormByTaskResponse;
				if (!task || !taskInfo) logger.error("Missing task response");

				const { taskId, processInstanceId, formPageNumber } = taskInfo;
				const { formKey } = task;

				const variables = await dispatch(fetchTaskVariables({ processInstanceId }));
				if (!fetchTaskVariables.fulfilled.match(variables)) logger.error("No task variables found");

				const variablesMap = Object.fromEntries(variables.payload?.data.map(({ name, value }) => [name, value]));

				const form =
					formPageNumber == null
						? await dispatch(fetchFormByID({ form_key: formKey }))
						: await dispatch(fetchFormByID({ form_key: formKey, pagenumber: formPageNumber, formData: variablesMap }));

				let formData = {
					...JSON.parse(form?.payload?.data?.formData || "{}"),
					processInstanceId,
					taskID: taskId,
					assignee: keycloakAuth.user.id,
					formKey,
				};

				if (formData?.settings?.data && typeof formData.settings.data === "object") {
					const parsedEntries = await Promise.all(
						Object.entries(formData.settings.data).map(async ([key, value]) => {
							return [key, await parseIfJson(value)];
						})
					);

					formData.settings.data = Object.fromEntries(parsedEntries);
				}

				localStorage.setItem(Constants.CurrentWorkflowStep, JSON.stringify(formData));
				navigate("/form_view_renderer", { state: { data: formData } });
			} else {
				logger.error("Failed to clone workflow");
			}
		} catch (error) {
			logger.error(error);
		}
	};
	// #endregion

	// #region VIEW PROCESS
	// ====================== VIEW PROCESS ======================
	const handleView = useCallback(
		async (processInstanceId, applicationID, tableId, formKey) => {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
			try {
				const variablesResult = await dispatch(fetchTaskVariables({ processInstanceId }));

				if (fetchTaskVariables.rejected.match(variablesResult)) {
					logger.error("Error fetching variables:", variablesResult.payload);
					dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
					return;
				}

				const variables = variablesResult.payload?.data;
				let result = {};

				if (variables.find((variable) => variable.name === "recordInTableId") !== undefined) {
					const recordResult = await dispatch(
						fetchDatabaseRecord({
							tableName: applicationID,
							fieldName: "id",
							itemId: tableId,
						})
					);

					if (fetchDatabaseRecord.fulfilled.match(recordResult)) {
						result = recordResult?.payload?.data?.record;
					}
				} else {
					result = variables.reduce((acc, item) => {
						acc[item.name] = item.value;
						return acc;
					}, {});
				}

				if (jQuery.isEmptyObject(result)) {
					const recordResult = await dispatch(
						fetchDatabaseRecord({
							tableName: applicationID,
							fieldName: "id",
							itemId: tableId,
						})
					);

					if (fetchDatabaseRecord.fulfilled.match(recordResult)) {
						result = recordResult?.payload?.data?.record;
					}
				}

				let form = null;

				if (result.formKey) {
					const formResult = await dispatch(
						fetchFormByID({
							form_key: result.formKey,
							pageNumber: null,
							formData: result,
							readonly: true,
							startingPage: 1,
						})
					);

					if (fetchFormByID.fulfilled.match(formResult)) {
						form = formResult.payload;
					}
				} else {
					ToastError("Form not found");
					dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
					return;
				}
				/* else if (formKey !== null) {
				const formResult = await dispatch(fetchFormByID({
					form_key: formKey,
					pageNumber: null,
					formData: result,
					readonly: true,
					startingPage: 1
				}));

				if (fetchFormByID.fulfilled.match(formResult)) {
					form = formResult.payload;
				}
			} else {
				return;
			} */

				// const formData = {
				// 	...JSON.parse(form.data.formData),
				// };

				if (form.settings && form.settings.data) {
					const parsedData = Object.fromEntries(Object.entries(form.settings.data).map(([key, value]) => [key, parseIfJson(value)]));
					form.settings.data = parsedData;
				}

				sessionStorage.setItem("pageIndex", currentPageRef.current);
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
				navigate("/workflow-history-view", { state: { data: form } });
			} catch (error) {
				logger.error("Error in handleView:", error);
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[dispatch, navigate]
	);
	// #endregion

	// #region DOWNLOAD PDF

	/* const GetRender = (renderId) => {
		const win = window.open(renderId);
		setTimeout(() => win?.close(), 3000);
	}; */
	const GetRender = useCallback((renderId) => {
		const a = document.createElement("a");
		a.href = renderId;
		a.target = "_blank";
		a.download = "";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	}, []);
	const downloadPdf = useCallback(
		async (processInstanceId, table_name, tableCreatedId) => {
			try {
				const variablesResult = await dispatch(fetchTaskVariables({ processInstanceId }));

				const variables = variablesResult.payload?.data || [];

				var result = variables.reduce((result, item) => {
					result[item.name] = item.value;
					return result;
				}, {});

				if (jQuery.isEmptyObject(result)) {
					const recordResult = await dispatch(
						fetchDatabaseRecord({
							tableName: table_name,
							fieldName: "id",
							itemId: tableCreatedId,
						})
					);

					if (fetchDatabaseRecord.fulfilled.match(recordResult)) {
						const rec = recordResult?.payload?.data?.record;
						if (rec?.renderId) {
							GetRender(rec.renderId);
						}
					}
				}
			} catch (err) {
				logger.error("Failed to get task variables:", err);
			}
		},
		[dispatch, GetRender]
	);
	// #endregion

	// #region WORKFLOW HISTORY TABLE COLUMNS
	const actions = useMemo(() => ({
		onView: (row) => handleView(row.rootProcessInstanceId, row.applicationID, row.tableId, row.formKey),
		onDelete: (row) => handleDeleteClick(row.rootProcessInstanceId, row.taskName, row.applicationID, row?.createdId),
		onDownload: (row) => downloadPdf(row.rootProcessInstanceId, row.applicationID, row.tableId),
		onEdit: handleEditClick,
		onRegenerate: handleRegerateClick,
		onClone: (row) => handleCloneClick(row.applicationID, row.rootProcessInstanceId),
	}), [handleView, handleDeleteClick, downloadPdf, handleEditClick, handleRegerateClick, handleCloneClick]);

	const { columns } = useTableColumns('WorkflowHistory', { actions });
	// #endregion

	// #region RETURN  COMPONENT

	// Show loading ONLY on initial load when there's no data
	if (loading && !hasDataInStore.current) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
				<Loader variant="dots" message="Loading..." />
			</Box>
		);
	}

	if (error && !displayData?.length) {
		return <Alert severity="error">{error}</Alert>;
	}

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
				<Typography variant="h3" color="primary">
					{t("Workflow History")}
				</Typography>
				{state.loading.isBackgroundRefreshing && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Loader variant="circular" message="Loading..." size="small" />
					</Box>
				)}
			</Box>

			<Typography variant="body1" color="text.secondary" paragraph>
				{t("Track and review the history of all completed and ongoing workflow processes.")}
			</Typography>

			<Divider sx={{ my: 3 }} />

			{/* Filters */}
			<Box
				sx={{
					my: 2,
					p: 2,
					borderRadius: 2,
					bgcolor: "rgba(29,131,84,0.04)",
				}}
			>
				<SearchFilterBar
					mode="backend"
					searchValue={state.table.searchInput}
					onSearchChange={(v) => dispatchState({ type: "SET_SEARCH_INPUT", payload: v })}
					showDateRange
					startDateValue={state.table.startDateInput}
					endDateValue={state.table.endDateInput}
					onStartDateChange={(v) => dispatchState({ type: "SET_START_DATE_INPUT", payload: v })}
					onEndDateChange={(v) => dispatchState({ type: "SET_END_DATE_INPUT", payload: v })}
					showActions
					onApply={applyFilters}
					onClear={clearFilters}
					showFilterButton={false}
					showSearchButton={false}
				/>
			</Box>
			<Typography variant="body1" sx={{ mt: 1 }}>
				Total History: {WorkflowHistoryData?.totalCount || 0}
			</Typography>

			{!displayData || displayData?.length === 0 ? (
				<DataContainer data={[]} emptyStateIcon={CalendarIcon} emptyStateTitle="No History Tasks yet" emptyStateSubtitle="Wait for someone to start or complete a task that you are involved in" />
			) : (
				<Box sx={{ mt: 2 }}>
					<DataContainer
						data={displayData}
						columns={columns}
						onSort={handleSort}
						sortConfig={{ key: state.table.sortField, direction: state.table.sortDirection }}
						showRowNumbers={true}
						maxHeight={600}
						stickyHeader={true}
					/>
				</Box>
			)}
			<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
				<Stack direction="row" spacing={2} alignItems="center">
					<TextField
						select
						size="small"
						label={t("Per page")}
						value={recordsPerPage}
						onChange={handleChangeRecordsPerPage}
						sx={{
							minWidth: 120,
							"& .MuiOutlinedInput-root": {
								"& fieldset": { borderColor: "primary.main" },
								"&:hover fieldset": { borderColor: "primary.main" },
								"&.Mui-focused fieldset": { borderColor: "primary.main" },
							},
						}}
					>
						{[5, 10, 15].map((n) => (
							<MenuItem key={n} value={n}>
								{n}
							</MenuItem>
						))}
					</TextField>

					<CustomPagination
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={(event, value) => setCurrentPage(value)}
						color="primary"
						showFirstButton
						showLastButton
						sx={{ mt: 2 }}
					/>
				</Stack>
			</Box>
			{/* Delete Confirmation Dialog */}
			{/* <ConfirmationDialog
                open={deleteConfirmOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Confirm Delete"
                message="Are you sure you want to delete this workflow history record? This action cannot be undone."
                cancelText="Cancel"
                confirmText="Delete"
                confirmColor="error"
            /> */}
			<EnhancedConfirmationDialog
				open={state.dialogs.deleteConfirm}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title="Confirm Delete"
				message="Are you sure you want to delete this workflow history record? This action cannot be undone."
				confirmText="DELETE"
				confirmColor="error"
				cancelText="Cancel"
			/>
			<EnhancedConfirmationDialog
				open={state.dialogs.cloneConfirm}
				onClose={handleCancelClone}
				onConfirm={handleConfirmClone}
				title="Confirm Clone"
				message="Are you sure you want to clone this workflow?"
				confirmText="CLONE"
				confirmColor="success"
				cancelText="Cancel"
			/>
			{/* <ConfirmationDialog
                open={acceptConfirmOpen}
                onClose={handleCancel}
                onConfirm={handleAccept}
                title="Confirm Acceptance"
                message="Are you sure you want to accept this request?"
                confirmText="Accept"
                confirmColor="success"
            /> */}
			{/* <ConfirmationDialog
                open={submitConfirmOpen}
                onClose={handleCancel}
                onConfirm={handleSubmit}
                title="Confirm Submission"
                message="Once submitted, you cannot edit this form. Do you want to proceed?"
                confirmText="Submit"
                confirmColor="primary"
            /> */}
			{/* <ConfirmationDialog
                open={confirmOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Warning"
                message="This action may have consequences. Are you sure?"
                confirmText="Proceed"
                confirmColor="warning"
            /> */}

			<EnhancedConfirmationDialog
				open={state.dialogs.editConfirm}
				onClose={handleCancelEdit}
				onConfirm={handleConfirmEdit}
				title="Reassign Driver" // "Reassign Driver" in Arabic
				message="Choose the new driver" // "Choose the new driver" in Arabic
				cancelText="Cancel" // "Cancel"
				confirmText="Modify" // "Modify"
				confirmColor="primary"
				showMessage={false}
				fields={[
					{
						name: "Driver",
						label: "Choose the new driver", // "Choose the new driver"
						type: "select",
						required: true,
						errorMessage: "Please select a driver",
						options: driverOptions,
					},
					{
						name: "Plate",
						label: "Choose the new truck", // "Choose the new truck"
						type: "select",
						required: true,
						errorMessage: "Please select a truck",
						options: trucksOptions,
					},
					/*                     {
											name: 'notes',
											label: 'Notes',
											type: 'text',
											multiline: true,
											rows: 3,
											placeholder: 'Add any additional notes...'
										} */
				]}
				formData={state.forms.reassignFormData}
				onFieldChange={handleFieldChange("reassignFormData")}
			/>

			<EnhancedConfirmationDialog
				open={state.dialogs.regenerateConfirm}
				onClose={handleCancelRegenerate}
				onConfirm={handleConfirmRegenerate}
				title="Regenerate PDF"
				message="Choose Templates"
				cancelText="Cancel"
				confirmText="Regenerate PDF"
				confirmColor="primary"
				showMessage={false}
				fields={[
					{
						name: "Template",
						label: "Choose Template",
						type: "select",
						required: true,
						options: templateOptions,
					},
				]}
				formData={state.forms.templateFormData}
				onFieldChange={handleFieldChange("templateFormData")}
			/>
			{/* <LoadingBackdrop
                open={isLoadingBackdrop}
                message="Loading data, please wait..."
            /> */}
			<LoadingBackdrop open={state.loading.isLoadingBackdrop} message="Loading data, please wait..." variant="bars" color="greenGradient" />
		</Box>
	);
	// #endregion
}
